import { tokenManager } from '../utils/api';
import { USER_URL } from '../config/environment';
import axios from 'axios';

/**
 * Account Management Service
 * Handles all account-related API calls
 */

const accountConfigCache = new Map();
const pendingAccountConfigRequests = new Map();

// Generic memo cache with TTL support to avoid repeated API calls across pages
const memoCache = new Map(); // key -> { value, expires }
const defaultTTL = 60 * 1000; // 60 seconds

const getCached = (key) => {
  const entry = memoCache.get(key);
  if (!entry) return null;
  if (entry.expires && Date.now() > entry.expires) {
    memoCache.delete(key);
    return null;
  }
  return entry.value;
};

const setCached = (key, value, ttlMs = defaultTTL) => {
  memoCache.set(key, { value, expires: ttlMs ? Date.now() + ttlMs : null });
};

const invalidateByPrefix = (prefix) => {
  for (const key of memoCache.keys()) {
    if (key.startsWith(prefix)) memoCache.delete(key);
  }
};

// Specific caches
const logoCache = new Map(); // fileName -> { blob, expires }
const logoTTL = 10 * 60 * 1000; // 10 minutes

export const accountService = {
  /**
   * Get account configurations
   * @returns {Promise<Object>} Account configurations
   */
  getAccountConfigs: async () => {
    const cacheKey = 'accountConfigs:list';
    const cached = getCached(cacheKey);
    if (cached) return cached;

    const response = await axios.get(`${USER_URL}/api/account-configs/account`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    const data = response.data;
    setCached(cacheKey, data);
    return data;
  },

  /**
   * Get account configuration by account ID
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Account configuration
   */
  getAccountConfigById: async (accountId) => {
    if (!accountId) {
      return null;
    }

    if (accountConfigCache.has(accountId)) {
      return accountConfigCache.get(accountId);
    }

    if (pendingAccountConfigRequests.has(accountId)) {
      return pendingAccountConfigRequests.get(accountId);
    }

    const promise = axios
      .get(`${USER_URL}/api/account-configs/${accountId}`, {
        headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
      })
      .then((response) => {
        const data = response.data;
        accountConfigCache.set(accountId, data);
        // also memoize under generic cache
        setCached(`accountConfig:${accountId}`, data);
        pendingAccountConfigRequests.delete(accountId);
        return data;
      })
      .catch((err) => {
        pendingAccountConfigRequests.delete(accountId);
        throw err;
      });

    pendingAccountConfigRequests.set(accountId, promise);
    return promise;
  },

  /**
   * Get all account configurations (Super Admin)
   * @returns {Promise<Array>} All account configurations
   */
  getAllAccountConfigs: async () => {
    const cacheKey = 'accountConfigs:all';
    const cached = getCached(cacheKey);
    if (cached) return cached;

    const response = await axios.get(`${USER_URL}/api/account-configs/getAll`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    const data = response.data;
    setCached(cacheKey, data);
    return data;
  },

  /**
   * Update account configuration
   * @param {Object} configData - Configuration data
   * @returns {Promise<Object>} Updated configuration
   */
  updateAccountConfig: async (configData) => {
    
    if (!configData.id) {
      console.error("Account ID is missing from config data:", configData);
      throw new Error("Account ID is required for updating account configuration");
    }
    
    const response = await axios.post(`${USER_URL}/api/account-configs/update`, configData, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    // Invalidate caches related to account configs
    invalidateByPrefix('accountConfigs');
    if (configData.id) {
      memoCache.delete(`accountConfig:${configData.id}`);
      memoCache.delete(`accountSummary:${configData.id}`);
      memoCache.delete(`domains:${configData.id}`);
    }
    return response.data;
  },

  /**
   * Upload file (generic file upload)
   * @param {FormData} formData - File data
   * @returns {Promise<Object>} Upload response
   */
  uploadFile: async (formData) => {
    const response = await axios.post(`${USER_URL}/api/file/upload`, formData, {
    });
    return response.data;
  },

  /**
   * Download company logo
   * @param {string} fileName - Logo file name
   * @returns {Promise<Blob>} Logo file blob
   */
  downloadLogo: async (fileName) => {
    try {
      const now = Date.now();
      const existing = logoCache.get(fileName);
      if (existing && existing.expires > now) {
        return existing.blob;
      }
      
      // First try to get as JSON to see the actual response format
      const response = await axios.get(`${USER_URL}/api/file/download/${fileName}`, {
        headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` },
        responseType: 'json'
      });
      
      
      // Check if the response contains the image data as base64 string
      if (response.data && response.data.success && response.data.data) {
        const base64Data = response.data.data;
        
        // Determine the MIME type based on the file extension
        let mimeType = 'image/png'; // default
        if (fileName.toLowerCase().includes('.jpg') || fileName.toLowerCase().includes('.jpeg')) {
          mimeType = 'image/jpeg';
        } else if (fileName.toLowerCase().includes('.webp')) {
          mimeType = 'image/webp';
        } else if (fileName.toLowerCase().includes('.gif')) {
          mimeType = 'image/gif';
        }
        
        // Create a proper data URL
        const dataUrl = `data:${mimeType};base64,${base64Data}`;
        
        // Convert data URL to blob for consistency with existing code
        const response2 = await fetch(dataUrl);
        const blob = await response2.blob();
        
        
        logoCache.set(fileName, { blob, expires: now + logoTTL });
        return blob;
      } else {
        console.error('accountService.downloadLogo - invalid response format:', response.data);
        throw new Error('API returned invalid response format');
      }
    } catch (error) {
      console.error('accountService.downloadLogo - error:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },

  /**
   * Get domains by account ID
   * @param {string} accountId - Account ID
   * @returns {Promise<Array>} Domains list
   */
  getDomainsByAccount: async (accountId) => {
    const cacheKey = `domains:${accountId}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;

    const response = await axios.get(`${USER_URL}/api/account-configs/${accountId}/domains`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    const data = response.data.data || [];
    setCached(cacheKey, data);
    return data;
  },

  /**
   * Add domain to account
   * @param {string} accountId - Account ID
   * @param {string} domain - Domain to add
   * @returns {Promise<Object>} Add domain response
   */
  addDomainToAccount: async (accountId, domain) => {
    const response = await axios.post(`${USER_URL}/api/account-configs/${accountId}/domains`, {
      domain
    }, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    // Invalidate domain and related caches
    memoCache.delete(`domains:${accountId}`);
    memoCache.delete(`accountConfig:${accountId}`);
    memoCache.delete(`accountSummary:${accountId}`);
    invalidateByPrefix('accountConfigs');
    return response.data;
  },

  /**
   * Delete domain
   * @param {string} domainId - Domain ID
   * @returns {Promise<Object>} Delete response
   */
  deleteDomain: async (domainId) => {
    const response = await axios.delete(`${USER_URL}/api/account-configs/domains/${domainId}`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    // Broad invalidation since we don't have the accountId here
    invalidateByPrefix('domains:');
    invalidateByPrefix('accountConfigs');
    return response.data;
  },
  
  /**
   * Update domain
   * @param {string} domainId - Domain ID
   * @param {Object} data - Update data containing domain field
   * @returns {Promise<Object>} Update response
   */
  updateDomain: async (domainId, data) => {
    const response = await axios.put(`${USER_URL}/api/account-configs/domains/${domainId}`, data, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    invalidateByPrefix('domains:');
    invalidateByPrefix('accountConfigs');
    return response.data;
  },

  /**
   * Get sender IDs by account ID
   * @param {string} accountId - Account ID
   * @returns {Promise<Array>} Sender IDs list
   */
  getSenderIdsByAccount: async (accountId) => {
    const cacheKey = `senderIds:${accountId}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;

    const response = await axios.get(`${USER_URL}/api/account-configs/${accountId}/sender-ids`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    const data = response.data.data || [];
    setCached(cacheKey, data);
    return data;
  },

  /**
   * Add sender ID to account
   * @param {string} accountId - Account ID
   * @param {string} senderId - Sender ID to add
   * @returns {Promise<Object>} Add sender ID response
   */
  addSenderIdToAccount: async (accountId, senderId) => {
    const response = await axios.post(`${USER_URL}/api/account-configs/${accountId}/sender-ids`, {
      senderId
    }, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    memoCache.delete(`senderIds:${accountId}`);
    memoCache.delete(`accountSummary:${accountId}`);
    return response.data;
  },

  /**
   * Delete sender ID
   * @param {string} senderId - Sender ID
   * @returns {Promise<Object>} Delete response
   */
  deleteSenderId: async (senderId) => {
    const response = await axios.delete(`${USER_URL}/api/account-configs/sender-ids/${senderId}`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    invalidateByPrefix('senderIds:');
    invalidateByPrefix('accountSummary:');
    return response.data;
  },
  
  /**
   * Update sender ID
   * @param {string} senderIdId - Sender ID identifier
   * @param {Object} data - Update data containing senderId field
   * @returns {Promise<Object>} Update response
   */
  updateSenderId: async (senderIdId, data) => {
    const response = await axios.put(`${USER_URL}/api/account-configs/sender-ids/${senderIdId}`, data, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    invalidateByPrefix('senderIds:');
    invalidateByPrefix('accountSummary:');
    return response.data;
  },

  /**
   * Create new account/company
   * @param {Object} companyData - Company data
   * @returns {Promise<Object>} Created company data
   */
  createAccount: async (companyData) => {
    const response = await axios.post(`${USER_URL}/api/account-configs/create`, companyData, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    invalidateByPrefix('accountConfigs');
    return response.data;
  },

  /**
   * Get account summary
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Account summary data
   */
  getAccountSummary: async (accountId) => {
    const cacheKey = `accountSummary:${accountId}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;

    const response = await axios.get(`${USER_URL}/api/account-configs/${accountId}/summary`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    const data = response.data;
    setCached(cacheKey, data);
    return data;
  },

  /**
   * Get auth account data
   * @returns {Promise<Object>} Auth account data
   */
  getAuthAccount: async () => {
    const cacheKey = 'authAccount';
    const cached = getCached(cacheKey);
    if (cached) return cached;

    const response = await axios.get(`${USER_URL}/api/auth/account`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    const data = response.data;
    setCached(cacheKey, data);
    return data;
  },

  /**
   * Get campaigns by account ID
   * @param {string} accountId - Account ID
   * @returns {Promise<Array>} List of campaigns
   */
  getCampaignsByAccountId: async (accountId) => {
    const cacheKey = `campaigns:${accountId}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;

    const response = await axios.get(`${USER_URL}/api/account-configs/${accountId}/campaigns`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    const data = response.data;
    setCached(cacheKey, data);
    return data;
  },

  /**
   * Get account data (campaigns, sender IDs, individual IDs) by account ID
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Object containing campaigns, senderIds, and individualIds arrays
   */
  getAccountDataByAccountId: async (accountId) => {
    const cacheKey = `accountData:${accountId}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;

    const response = await axios.get(`${USER_URL}/api/account-configs/${accountId}/account-data`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    const data = response.data;
    setCached(cacheKey, data);
    return data;
  }
};

export default accountService;
